import { useEffect, useMemo, useState } from "react"

import { usePostSearchCount } from "openapi/api/endpoints"
import type { BeginSearchRequest } from "openapi/api/model"

import type { SearchFiltersDraft } from "@/lib/search-filter-model"
import { mapSearchFiltersToSearchPayload } from "@/utils/search-utils"

import { useDebounce } from "./useDebounce"

interface UseSearchCountEstimateOptions {
  enabled?: boolean
  filters: SearchFiltersDraft
  prompt: string
}

/**
 * Fetches the candidate estimate for the currently accepted prompt and filter draft.
 * Count failures are intentionally non-blocking so the search can still be reviewed.
 */
export function useSearchCountEstimate({
  enabled = true,
  filters,
  prompt,
}: UseSearchCountEstimateOptions) {
  const [estimatedCount, setEstimatedCount] = useState<number | null>(null)
  const [countError, setCountError] = useState(false)
  const [isCounting, setIsCounting] = useState(false)
  const { mutateAsync: getCandidateCount } = usePostSearchCount()

  const request = useMemo(() => {
    const trimmedPrompt = prompt.trim()
    if (!enabled || !trimmedPrompt) return null

    return {
      payload: mapSearchFiltersToSearchPayload(filters),
      prompt: trimmedPrompt,
    }
  }, [enabled, filters, prompt])

  const debouncedRequest = useDebounce(request, 500)

  useEffect(() => {
    if (!debouncedRequest) {
      setEstimatedCount(null)
      setCountError(false)
      setIsCounting(false)
      return
    }

    let cancelled = false

    const runCount = async () => {
      try {
        setIsCounting(true)
        setCountError(false)

        const response = await getCandidateCount({
          data: {
            ...debouncedRequest.payload,
            prompt: debouncedRequest.prompt,
          } as BeginSearchRequest,
        })

        if (!cancelled) {
          setEstimatedCount(response.total_results ?? 0)
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch candidate estimate", error)
          setEstimatedCount(null)
          setCountError(true)
        }
      } finally {
        if (!cancelled) {
          setIsCounting(false)
        }
      }
    }

    void runCount()

    return () => {
      cancelled = true
    }
  }, [debouncedRequest, getCandidateCount])

  return {
    countError,
    estimatedCount,
    isCounting,
  }
}
